import React from "react";
import { Row, Col, Card, Table, Badge, Button } from "react-bootstrap";
import {
  FaBlog,
  FaFileAlt,
  FaEnvelope,
  FaArrowRight,
  FaPlus,
  FaChartLine,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useGetDashboardStatsQuery } from "../../../redux/api/DashboardApi/dashboardApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";

const Dashboard = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetDashboardStatsQuery();

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <LoaderSpinner />
      </div>
    );
  }

  const { stats, recent } = data || { stats: {}, recent: {} };

  const statCards = [
    {
      title: "Total Blogs",
      value: stats.blogs || 0,
      icon: <FaBlog />,
      color: "#4e73df",
      link: "/admin/blogs",
    },
    {
      title: "Applications",
      value: stats.applications || 0,
      icon: <FaFileAlt />,
      color: "#1cc88a",
      link: "/admin/applications",
    },
    {
      title: "Total Works",
      value: stats.works || 0,
      icon: <FaChartLine />,
      color: "#36b9cc",
      link: "/admin/works",
    },
    {
      title: "Inquiries",
      value: stats.contacts || 0,
      icon: <FaEnvelope />,
      color: "#f6c23e",
      link: "/admin/contacts",
    },
  ];

  return (
    <div className="admin-dashboard p-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-1">Dashboard</h2>
          <p className="text-muted mb-0">
            Welcome back! Here's what's happening today.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="light"
            className="shadow-sm border rounded-pill px-4"
            onClick={() => navigate("/admin/blogs")}
          >
            View Blogs
          </Button>
          <Button
            variant="primary"
            className="shadow-sm rounded-pill px-4"
            onClick={() => navigate("/admin/add-blog")}
          >
            <FaPlus className="me-2" /> New Post
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <Row className="mb-4 g-4">
        {statCards.map((card, idx) => (
          <Col key={idx} xs={12} sm={6} lg={3}>
            <Card
              className="border-0 shadow-sm rounded-4 stat-card h-100 overflow-hidden"
              onClick={() => navigate(card.link)}
              style={{ cursor: "pointer" }}
            >
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div
                    className="stat-icon"
                    style={{
                      backgroundColor: `${card.color}20`,
                      color: card.color,
                    }}
                  >
                    {card.icon}
                  </div>
                  <Badge
                    bg="light"
                    className="text-muted py-2 px-3 border rounded-pill"
                  >
                    All Time
                  </Badge>
                </div>
                <h3 className="fw-bold mb-1">{card.value}</h3>
                <p className="text-muted small mb-0 font-weight-bold text-uppercase">
                  {card.title}
                </p>
              </Card.Body>
              <div style={{ height: "4px", backgroundColor: card.color }}></div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-4">
        {/* Recent Applications */}
        <Col lg={7}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Header className="bg-white border-bottom p-4 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0">Recent Applications</h5>
              <Button
                variant="link"
                className="p-0 text-decoration-none"
                onClick={() => navigate("/admin/applications")}
              >
                View All <FaArrowRight size={12} className="ms-1" />
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="align-middle mb-0 custom-table">
                  <thead className="bg-light text-muted small text-uppercase">
                    <tr>
                      <th className="ps-4">Applicant</th>
                      <th>Job Position</th>
                      <th>Applied On</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.applications?.length > 0 ? (
                      recent.applications.map((app) => (
                        <tr
                          key={app.id}
                          onClick={() =>
                            navigate(`/admin/application-detail/${app.id}`)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <td className="ps-4 py-3">
                            <div className="fw-bold">{app.name}</div>
                            <div className="extra-small text-muted">
                              {app.email}
                            </div>
                          </td>
                          <td>
                            <Badge bg="light" className="text-secondary border">
                              {app.job?.title || "General"}
                            </Badge>
                          </td>
                          <td className="small text-muted">
                            {new Date(app.createdAt).toLocaleDateString()}
                          </td>
                          <td className="text-center text-capitalize">
                            <Badge
                              bg={
                                app.status === "pending" ? "warning" : "success"
                              }
                              pill
                            >
                              {app.status || "Received"}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-5 text-muted">
                          No recent applications found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Inquiries */}
        <Col lg={5}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Header className="bg-white border-bottom p-4 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold mb-0">Recent Contact Inquiries</h5>
              <Button
                variant="link"
                className="p-0 text-decoration-none"
                onClick={() => navigate("/admin/contacts")}
              >
                See Details
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="p-1">
                {recent.contacts?.length > 0 ? (
                  recent.contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="p-3 border-bottom d-flex align-items-center gap-3 inquiry-item"
                      onClick={() => navigate("/admin/contacts")}
                      style={{ cursor: "pointer" }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center bg-light text-primary"
                        style={{ width: "45px", height: "45px", flexShrink: 0 }}
                      >
                        <FaEnvelope />
                      </div>
                      <div className="overflow-hidden">
                        <div className="fw-bold text-truncate">
                          {contact.name}
                        </div>
                        <div className="extra-small text-muted text-truncate">
                          {contact.message?.substring(0, 60)}...
                        </div>
                        <div className="extra-small text-primary mt-1">
                          {new Date(contact.createdAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-5 text-muted">
                    No inquiries yet
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Blogs */}
        <Col lg={12}>
          <Card className="border-0 shadow-sm rounded-4 mb-4">
            <Card.Header className="bg-white border-bottom p-4">
              <h5 className="fw-bold mb-0">Recent Blog Status</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="align-middle mb-0 custom-table">
                  <thead className="bg-light text-muted small text-uppercase">
                    <tr>
                      <th className="ps-4">Blog Title</th>
                      <th>Published Date</th>
                      <th>View Count</th>
                      <th className="text-end pe-4">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.blogs?.map((blog) => (
                      <tr
                        key={blog.id}
                        onClick={() => navigate(`/admin/edit-blog/${blog.id}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="ps-4 py-3 fw-bold">{blog.title}</td>
                        <td className="small text-muted">
                          {new Date(blog.createdAt).toLocaleDateString()}
                        </td>
                        <td>{blog.view_count || 0} views</td>
                        <td className="text-end pe-4">
                          <FaChartLine className="text-success" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <style>{`
        .stat-card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .inquiry-item {
          transition: background 0.2s ease;
        }
        .inquiry-item:hover {
          background-color: #f8f9fa;
        }
        .extra-small {
           font-size: 11px;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
